<template>
  <div ref="whatsappContainer" class="whatsapp-chat-container"></div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick } from 'vue';
import { useRuntimeConfig } from 'nuxt/app';

interface WhatsAppResponse {
  success: boolean;
  html?: string;
  message?: string;
}

const { $ajax } = useNuxtApp();
const whatsappContainer = ref<HTMLDivElement | null>(null);
const isLoaded = ref<boolean>(false);

const loadWhatsAppChat = async (): Promise<void> => {
  if (isLoaded.value) return;

  try {
    const response = await $ajax.get<WhatsAppResponse>('wp-json/alt/v1/whatsapp-html');

    if (response.success && response.html) {
      // Create a div to hold the WhatsApp HTML
      const whatsappDiv = document.createElement('div');
      whatsappDiv.innerHTML = response.html;

      // Append the WhatsApp HTML to our container
      if (whatsappContainer.value) {
        whatsappContainer.value.appendChild(whatsappDiv);

        // Load required scripts and styles
        const runtimeConfig = useRuntimeConfig();

        // Load WhatsApp styles
        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = runtimeConfig.public.BASE_URL + '/api/wp-content/plugins/wp-whatsapp-chat/build/frontend/css/style.css';
        cssLink.type = 'text/css';
        document.head.appendChild(cssLink);

        // Load WhatsApp scripts
        const script = document.createElement('script');
        script.src = runtimeConfig.public.BASE_URL + '/api/wp-content/plugins/wp-whatsapp-chat/build/frontend/js/index.js';
        script.onload = () => {
          //console.log('WhatsApp Chat script loaded');

          // Initialize any required WhatsApp chat functionality
          if (window.qlwapp && typeof window.qlwapp.init === 'function') {
            window.qlwapp.init();
          }
            // Set up chat button click handlers after script is loaded
            setupChatButtonListeners();
        };
        document.head.appendChild(script);

        isLoaded.value = true;
      }
    } else {
      console.error('Failed to load WhatsApp HTML:', response);
    }
  } catch (error) {
    console.error('Error loading WhatsApp chat:', error);
  }
};

// Function to open WhatsApp chat programmatically
const openWhatsApp = (): void => {
  const whatsapp = document.getElementById("qlwapp");
  if (whatsapp && !whatsapp.classList.contains("qlwapp-show")) {
    const whatsappToggle = whatsapp.querySelector(".qlwapp-toggle");
    if (whatsappToggle) {
      (whatsappToggle as HTMLElement).click();
    }
  }
};

// Function to set up chat button click listeners
const setupChatButtonListeners = (): void => {
  const chatButton = document.getElementById("kts-faq-chatlink");

  if (chatButton) {
    chatButton.addEventListener("click", (e) => {
      e.preventDefault();
      openWhatsApp();
    });
  }

  // You can add more button listeners here if needed
};

const initWhatsAppOnUserAction = (): void => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    // For mobile: Load on first touch
    document.addEventListener('touchstart', () => {
      // loadWhatsAppChat();
    }, { once: true });
  } else {
    // For desktop: Load on mousemove
    document.addEventListener('mousemove', () => {
      // loadWhatsAppChat();
    }, { once: true });
  }
};

onMounted(() => {
  nextTick(() => {
    initWhatsAppOnUserAction();
  });
});

// Add TypeScript declaration for the WhatsApp plugin's global object
declare global {
  interface Window {
    qlwapp?: {
      init: () => void;
    };
  }
}
</script>

<style lang="scss">
.whatsapp-chat-container {
  position: fixed;
  z-index: 1000; /* Ensure it's above other elements */

  #qlwapp {
    &.qlwapp-bottom-right {
      bottom: 75px;
      @media (max-width: 768px) {
        bottom: 60px;
      }
    }
  }
  .qlwapp-bottom-right {
    bottom: 75px !important;
      @media (max-width: 768px) {
        bottom: 60px !important;
      }
  }
}
</style>